import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Link, Box, Tooltip } from '@mui/material';
import { alpha, lighten, Theme } from '@mui/material/styles';
import {
    SwapHoriz as TransferableIcon,
    ShoppingCart as OnlineIcon,
    CreditCard as TpvIcon,
    EventBusy as ExpirationIcon,
    Group as GroupIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButtonFab,
    SttTable,
    SttTopControls,
    SttLabelPeriod,
    SttLabelWeekdays,
    SttPage,
} from '../../../sporttia/all';
import { useToggle, useTuples, useCrud } from '../../../lib/hooks';
import { sporttiaColors } from '../../../theme/theme';
import { SttHasDeviceIcon } from '../../../sporttia/icons/SttHasDeviceIcon';
import { onLight } from '../../../lib/contrast';
import BonoDialog from './Bono';
import { useBonosTour } from 'src/lib/tours/useBonosTour';
import { PERMISSIONS } from '../../../lib/permissions';

const formatTime = (time: string): string => {
    if (!time) return '';
    const parts = time.split(':');
    return `${parts[0]}:${parts[1]}`;
};

type BonoChipType = 'COMPLETE' | 'SINGLE';

type BonoItem = {
    id: number;
    name: string;
    type: BonoChipType;
    dateini: string;
    dateend: string;
    weekdays: number[];
    timeini: string;
    timeend: string;
    amount: number;
    duration: number;
    expirationLang?: string;
    numSold: number;
    numActive: number;
    numConsumptions: number;
    price: number;
    totalIncome: number;
    transferable: boolean;
    purchaseOnline: boolean;
    purchasePFs: string[];
    hasDevice?: boolean;
    trash?: boolean;
};

type BonosResponse = {
    rows: BonoItem[];
    count: number;
    page: number;
    pages: number;
};

type BonoState = {
    id: number | 'create' | null;
    trash: boolean;
};

type FetchParams = {
    page: number;
    rows: number;
};

/**
 * Colores de tipo de bono y el estilo del icono sobre su propio círculo teñido
 * al 15 %. En claro el círculo queda pálido y el icono hay que oscurecerlo
 * (`onLight`); en oscuro el círculo queda oscuro y lo que se lee es el tono
 * aclarado — con el color a pelo se quedaba en 2,77:1.
 *
 * Va con `theme.applyStyles` y no con `theme.palette.mode`: el tema usa
 * `cssVariables`, así que el modo lo decide una clase en el HTML y en un
 * callback de `sx` el `mode` siempre informa del esquema por defecto.
 */
const BONO_COMPLETE = '#337788';
const BONO_SINGLE = '#a94aaf';

function iconoBonoSx(color: string) {
    return (theme: Theme) => ({
        fontSize: 18,
        color: onLight(color),
        ...theme.applyStyles('dark', { color: lighten(color, 0.55) }),
    });
}

export default function Bonos(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { startTour } = useBonosTour();

    const [bonos, setBonos, , Get] = useCrud<BonosResponse>();
    const [selectedBono, setSelectedBono] = useState<BonoState>({
        id: null,
        trash: false,
    });

    const [filters, setFilter] = useTuples({
        name: '',
    });

    const [trash, toggleTrash] = useToggle(undefined);
    const [onTime, toggleOnTime] = useToggle(undefined, true);

    const prevTrashRef = useRef(trash);
    const prevOnTimeRef = useRef(onTime);

    useEffect(() => {
        cxt.setHeader({ title: t('Bonos') });
    }, [t]);

    const loadBonos = useCallback(
        (params: FetchParams = { page: 1, rows: 20 }): void => {
            Get<BonosResponse>(`/scs/${cxt.sc?.id}/bonos`, {
                ...params,
                ...filters,
                trash,
                onTime,
            }).then((response) => setBonos(response));
        },
        [Get, cxt.sc?.id, filters, trash, onTime, setBonos],
    );

    // Load bonos when trash or onTime actually changes value
    useEffect(() => {
        const trashChanged = prevTrashRef.current !== trash;
        const onTimeChanged = prevOnTimeRef.current !== onTime;

        prevTrashRef.current = trash;
        prevOnTimeRef.current = onTime;

        if (trashChanged || onTimeChanged) {
            loadBonos();
        }
    }, [trash, onTime]);

    const handleCreateNew = (): void => {
        setSelectedBono({ id: 'create', trash: false });
    };

    const handleBonoClick = useCallback((id: number, trash: boolean): void => {
        setSelectedBono({ id, trash });
    }, []);

    return (
        <SttPage title={t('Bonos')} help={startTour}>
            <SttTopControls
                p={2}
                fields={[
                    {
                        caption: t('Name'),
                        type: 'search',
                        name: 'name',
                        value: filters.name,
                        onEnterKey: loadBonos,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                    {
                        caption: t('Status'),
                        type: 'select',
                        name: 'onTime',
                        value: (+onTime).toString(),
                        mandatory: false,
                        cleanable: false,
                        options: [
                            {
                                label: t('All'),
                                value: 0,
                            },
                            {
                                label: t('InForce'),
                                value: 1,
                            },
                        ],
                        onChange: ({
                            value,
                        }: {
                            name: string | undefined;
                            value: unknown;
                        }) => {
                            toggleOnTime(!!parseInt(value as string, 10));
                        },
                        md: 4,
                        sm: 4,
                        xs: 12,
                        lg: 4,
                    },
                ]}
                onToggleTrash={toggleTrash}
                onChange={setFilter}
                onFilter={loadBonos}
            />
            <SttTable
                onFetch={loadBonos}
                data={bonos}
                columns={[
                    {
                        title: t('Name'),
                        align: 'left',
                        value: (row: BonoItem) => (
                            <Link
                                component="button"
                                onClick={() =>
                                    handleBonoClick(row.id, row.trash || false)
                                }
                                sx={{
                                    color: 'var(--brand-text)',
                                    textDecoration: 'none',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                    cursor: 'pointer',
                                    border: 'none',
                                    background: 'none',
                                    padding: 0,
                                    textAlign: 'left',
                                }}
                            >
                                {row.name}
                            </Link>
                        ),
                    },
                    {
                        title: t('Type'),
                        align: 'center',
                        width: 60,
                        value: (row: BonoItem) => {
                            const isComplete = row.type === 'COMPLETE';
                            return (
                                <Tooltip title={t(`BT.${row.type}`)}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: isComplete
                                                ? alpha(BONO_COMPLETE, 0.15)
                                                : alpha(BONO_SINGLE, 0.15),
                                        }}
                                    >
                                        {isComplete ? (
                                            <GroupIcon
                                                sx={iconoBonoSx(BONO_COMPLETE)}
                                            />
                                        ) : (
                                            <PersonIcon
                                                sx={iconoBonoSx(BONO_SINGLE)}
                                            />
                                        )}
                                    </Box>
                                </Tooltip>
                            );
                        },
                    },
                    {
                        title: t('Period'),
                        value: (row: BonoItem) => (
                            <Box>
                                <SttLabelPeriod
                                    ini={row.dateini}
                                    end={row.dateend}
                                />
                                {row.weekdays && (
                                    <Box mt={0.5}>
                                        <SttLabelWeekdays
                                            weekdays={row.weekdays}
                                        />
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                        mt: 0.5,
                                    }}
                                >
                                    {formatTime(row.timeini)} -{' '}
                                    {formatTime(row.timeend)}
                                </Box>
                            </Box>
                        ),
                    },
                    {
                        title: t('Status'),
                        align: 'center',
                        value: (row: BonoItem) => {
                            const hasExpiration =
                                row.expirationLang &&
                                !row.expirationLang.startsWith('0000-00-00');
                            const hasTpv = row.purchasePFs?.includes('TPV');

                            return (
                                <Box className="tw-flex tw-gap-1 tw-justify-center">
                                    <Tooltip title={t('Transferable')}>
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                backgroundColor:
                                                    row.transferable
                                                        ? alpha(
                                                              sporttiaColors
                                                                  .primary.main,
                                                              0.15,
                                                          )
                                                        : sporttiaColors.icon
                                                              .disabledBg,
                                            }}
                                        >
                                            <TransferableIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: row.transferable
                                                        ? 'var(--brand-text)'
                                                        : sporttiaColors.icon
                                                              .disabled,
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title={t('OnlinePurchase')}>
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                backgroundColor:
                                                    row.purchaseOnline
                                                        ? alpha(
                                                              sporttiaColors
                                                                  .info.main,
                                                              0.15,
                                                          )
                                                        : sporttiaColors.icon
                                                              .disabledBg,
                                            }}
                                        >
                                            <OnlineIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: row.purchaseOnline
                                                        ? 'info.main'
                                                        : sporttiaColors.icon
                                                              .disabled,
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title={t('sc.config.tpvPurchase')}>
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                backgroundColor: hasTpv
                                                    ? alpha(
                                                          sporttiaColors.warning
                                                              .main,
                                                          0.15,
                                                      )
                                                    : sporttiaColors.icon
                                                          .disabledBg,
                                            }}
                                        >
                                            <TpvIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: hasTpv
                                                        ? 'warning.main'
                                                        : sporttiaColors.icon
                                                              .disabled,
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>
                                    <Tooltip
                                        title={
                                            hasExpiration
                                                ? `${t('Expiration')}: ${row.expirationLang}`
                                                : t('Expiration')
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                backgroundColor: hasExpiration
                                                    ? alpha(
                                                          sporttiaColors.error
                                                              .main,
                                                          0.15,
                                                      )
                                                    : sporttiaColors.icon
                                                          .disabledBg,
                                            }}
                                        >
                                            <ExpirationIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: hasExpiration
                                                        ? 'error.main'
                                                        : sporttiaColors.icon
                                                              .disabled,
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>
                                    <SttHasDeviceIcon
                                        hasDevice={row.hasDevice}
                                    />
                                </Box>
                            );
                        },
                    },
                    {
                        title: `${t('Uses')} / ${t('Price')}`,
                        type: 'text',
                        value: (row: BonoItem) => {
                            const price = parseFloat(String(row.price)) || 0;
                            return `${row.amount} x ${row.duration}m / ${price.toFixed(2)}€`;
                        },
                    },
                    {
                        title: `${t('Sold')} / ${t('Active')}`,
                        type: 'text',
                        value: (row: BonoItem) =>
                            `${row.numSold} / ${row.numActive}`,
                    },
                    {
                        title: `${t('Consumed')} / ${t('Income')}`,
                        type: 'text',
                        value: (row: BonoItem) => {
                            const numConsumptions = row.numConsumptions ?? 0;
                            const consumed =
                                numConsumptions < 0
                                    ? -numConsumptions
                                    : numConsumptions;
                            const income =
                                parseFloat(String(row.totalIncome)) || 0;
                            return `${consumed} con. / ${income.toFixed(2)}€`;
                        },
                    },
                ]}
            />
            <SttButtonFab
                onClick={handleCreateNew}
                permission={PERMISSIONS.VOUCHER.CREATE}
            />

            <BonoDialog
                id={selectedBono.id}
                trash={selectedBono.trash}
                onChange={(updatedBono) => {
                    if (typeof updatedBono.id === 'number' && bonos?.rows) {
                        const updatedId = updatedBono.id;
                        setBonos({
                            ...bonos,
                            rows: bonos.rows
                                .map((item) =>
                                    item.id === updatedId
                                        ? {
                                              ...item,
                                              ...updatedBono,
                                              id: updatedId,
                                          }
                                        : item,
                                )
                                .filter(
                                    (item) =>
                                        !!trash || (!trash && !item.trash),
                                ),
                        });
                    }
                }}
                onClose={() => {
                    setSelectedBono({ id: null, trash: false });
                    loadBonos();
                }}
            />
        </SttPage>
    );
}
